/**
 * Master Data Contact Form Modal
 * Modal for creating/editing contacts with Vendor/Customer tab selection
 *
 * board2-#79 「地址格式改成一個textfield，丟到googlet拆分」
 * (page 2cb89adb-b34d-80ee-a729-d094a38c45cc): address entry is
 * country (CascadingLocationFields) + postcode + ONE full-address textfield.
 * State/city Selects are gone — stateId/cityId stay in the form data only as
 * backend-parse OUTPUTS (Google Maps split, simulated by the mocks); the
 * postcode lookup may pre-resolve them, a country change clears them.
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Input,
  Select,
  Button,
  Typography,
  Radio,
  Row,
  Col,
  theme,
  App,
} from 'antd';
import { InfoCircleOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useFormStyles } from '@/styles/formStyles';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';
import { usePostcodeLookup } from '@/hooks/usePostcodeLookup';
import { useCountriesData } from '@/hooks/useLocations';
import { CascadingLocationFields } from '@/components/common/form/CascadingLocationFields';
import { contactFormModalSchema } from '@/schemas/forms';
import { getCountryCodeOptions, DEFAULT_COUNTRY_CODE } from '@/utils/phoneUtils';
import {
  useVendorsForDropdown,
  useCustomersForDropdown,
  useMasterDataContactMutations,
} from '@/hooks/useMasterDataContacts';
import { contactApi } from '@/services/api/contact.api';
import { customerApi } from '@/services/api/customer.api';
import type {
  ContactType,
  MasterDataContactDetail,
  MasterDataContactListItem,
  AffiliationCompanyType,
  ContactAffiliationInput,
} from '@/types/api/masterDataContact';
import { normalizeContactAffiliations } from '@/types/api/masterDataContact';

/**
 * #92: one editable affiliation row — the person can be linked to several
 * vendors/customers at once; the FIRST row is the primary affiliation.
 */
export interface AffiliationFormRow {
  companyType: AffiliationCompanyType;
  companyId: number | null;
  roleTitle: string;
}

export interface MasterDataContactFormData {
  name: string;
  isPrimary: boolean;
  jobTitle: string;
  affiliations: AffiliationFormRow[]; // #92 multi-affiliation list
  phoneCountryCode: string;
  phoneNumber: string;
  phoneExtension?: string;
  mobileCountryCode?: string;
  mobileNumber?: string;
  fax?: string;
  email: string;
  // Structured address fields - using IDs
  countryId?: number | null;
  stateId?: number | null;
  cityId?: number | null;
  // Location name labels (for edit mode pre-fill fallback)
  country?: string;
  state?: string;
  city?: string;
  postcode?: string;
  address?: string;
}

interface MasterDataContactFormModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  contact?: MasterDataContactDetail | null;
  /** List record with optional pre-resolved location names (for edit mode fallback) */
  listRecord?: MasterDataContactListItem | null;
  defaultTypeId?: ContactType;
  /** When true, displays form in read-only mode (view mode) */
  readOnly?: boolean;
}

// Default values for empty/create mode
const defaultContactValues: MasterDataContactFormData = {
  name: '',
  isPrimary: false,
  jobTitle: '',
  affiliations: [{ companyType: 'vendor', companyId: null, roleTitle: '' }],
  phoneCountryCode: DEFAULT_COUNTRY_CODE,
  phoneNumber: '',
  phoneExtension: '',
  mobileCountryCode: DEFAULT_COUNTRY_CODE,
  mobileNumber: '',
  fax: '',
  email: '',
  countryId: null,
  stateId: null,
  cityId: null,
  country: '',
  state: '',
  city: '',
  postcode: '',
  address: '',
};

const MasterDataContactFormModal = ({
  visible,
  onClose,
  onSuccess,
  contact,
  listRecord,
  defaultTypeId = 1,
  readOnly = false,
}: MasterDataContactFormModalProps) => {
  const { token } = theme.useToken();
  const { t } = useVendorTranslation();
  const { t: tContacts } = useTranslation('contacts');
  const { message, modal } = App.useApp();
  const formStyles = useFormStyles();
  const i18n = useTranslation().i18n;
  const isEditMode = !!contact;

  const [saving, setSaving] = useState(false);

  // Track existing primary contact for the selected company
  const [existingPrimaryContact, setExistingPrimaryContact] = useState<{ contactId: number; name: string } | null>(null);

  // Fetch vendors/customers for dropdown
  const { vendors, isLoading: vendorsLoading } = useVendorsForDropdown();
  const { customers, isLoading: customersLoading } = useCustomersForDropdown();

  // Mutations
  const {
    createVendorContact,
    createCustomerContact,
    updateVendorContact,
    updateCustomerContact,
    setVendorContactPrimary,
    setCustomerContactPrimary,
  } = useMasterDataContactMutations();

  // Fetch existing contacts to check for primary contact
  const fetchExistingPrimaryContact = useCallback(async (companyId: number, type: ContactType, excludeContactId?: number) => {
    try {
      if (type === 1) {
        // Vendor contacts
        const response = await contactApi.list(companyId);
        const primaryContact = response.data?.find(
          (c) => c.isPrimary && c.contactId !== excludeContactId
        );
        if (primaryContact) {
          setExistingPrimaryContact({ contactId: primaryContact.contactId, name: primaryContact.name });
        } else {
          setExistingPrimaryContact(null);
        }
      } else {
        // Customer contacts
        const response = await customerApi.getContacts(companyId);
        const primaryContact = response.data?.find(
          (c) => c.isPrimary && c.contactId !== excludeContactId
        );
        if (primaryContact) {
          setExistingPrimaryContact({ contactId: primaryContact.contactId, name: primaryContact.name });
        } else {
          setExistingPrimaryContact(null);
        }
      }
    } catch (error) {
      console.error('Error fetching existing contacts:', error);
      setExistingPrimaryContact(null);
    }
  }, []);

  // Reset existing primary when modal closes or company changes
  useEffect(() => {
    if (!visible) {
      setExistingPrimaryContact(null);
    }
  }, [visible]);

  // Read-only input style
  const readOnlyStyle = readOnly
    ? {
        backgroundColor: token.colorBgContainerDisabled,
        cursor: 'default',
      }
    : {};

  // Compute form values using useMemo
  const formValues = useMemo(() => {
    if (!visible) {
      return defaultContactValues;
    }
    if (contact) {
      // Map contact to form data - using MasterDataContactDetail which has all fields
      // Use country/state/city labels from contact or listRecord for edit mode pre-fill fallback
      return {
        name: contact.name,
        isPrimary: contact.isPrimary,
        jobTitle: contact.jobTitle || '',
        // #92: legacy single-org records normalize to a one-element list
        affiliations: normalizeContactAffiliations(contact).map((a) => ({
          companyType: a.companyType,
          companyId: a.companyId,
          roleTitle: a.roleTitle ?? '',
        })),
        phoneCountryCode: contact.phoneCountryCode || DEFAULT_COUNTRY_CODE,
        phoneNumber: contact.phoneNumber || '',
        phoneExtension: contact.phoneExtension || '',
        mobileCountryCode: contact.mobileCountryCode || DEFAULT_COUNTRY_CODE,
        mobileNumber: contact.mobileNumber || '',
        fax: contact.fax || '',
        email: contact.email,
        // Address fields - use IDs from contact detail
        countryId: contact.countryId ?? null,
        stateId: contact.stateId ?? null,
        cityId: contact.cityId ?? null,
        // Use labels from contact first, then fallback to listRecord for edit mode pre-fill
        country: contact.country || listRecord?.country || '',
        state: contact.state || listRecord?.state || '',
        city: contact.city || listRecord?.city || '',
        postcode: contact.postcode || '',
        address: contact.address || '',
      };
    }
    // Create mode: seed the first affiliation row from the list page's tab
    return {
      ...defaultContactValues,
      affiliations: [
        {
          companyType: (defaultTypeId === 2 ? 'customer' : 'vendor') as AffiliationCompanyType,
          companyId: null,
          roleTitle: '',
        },
      ],
    };
  }, [visible, contact, listRecord, defaultTypeId]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, touchedFields, isSubmitted },
  } = useForm<MasterDataContactFormData>({
    resolver: zodResolver(contactFormModalSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: defaultContactValues,
    values: formValues,
  });

  // #92: editable multi-affiliation rows (first row = primary affiliation)
  const {
    fields: affiliationFields,
    append: appendAffiliation,
    remove: removeAffiliation,
  } = useFieldArray({ control, name: 'affiliations' });

  // Watch countryId for postcode lookup
  const selectedCountryId = watch('countryId');
  const watchedAffiliations = watch('affiliations');
  const primaryAffiliation = watchedAffiliations?.[0];
  const primaryCompanyId = primaryAffiliation?.companyId ?? null;
  const primaryCompanyType: ContactType = primaryAffiliation?.companyType === 'customer' ? 2 : 1;
  const { countries } = useCountriesData();

  // Fetch existing primary contact for the PRIMARY affiliation's company
  // (the isPrimary radio means "primary contact of that company")
  useEffect(() => {
    if (visible && primaryCompanyId) {
      fetchExistingPrimaryContact(primaryCompanyId, primaryCompanyType, contact?.contactId);
    } else if (visible && !primaryCompanyId) {
      setExistingPrimaryContact(null);
    }
  }, [visible, primaryCompanyId, primaryCompanyType, contact, fetchExistingPrimaryContact]);

  // Resolve countryCode from countryId for postcode lookup
  const selectedCountryCode = useMemo(() => {
    if (!selectedCountryId || !countries) return undefined;
    const country = countries.find((c) => c.countryId === selectedCountryId);
    return country?.countryCode;
  }, [selectedCountryId, countries]);

  // Postcode lookup hook — pre-resolves the state/city PARSE OUTPUTS when the
  // postcode is on the seeded dataset (board2-#79); unresolved rows are left
  // for the backend parse on save
  const { handlePostcodeChange, handlePostcodeBlur } = usePostcodeLookup({
    countryCode: selectedCountryCode,
    setValue: setValue as (name: string, value: unknown) => void,
    stateIdField: 'stateId',
    cityIdField: 'cityId',
  });

  // Helper function to perform the actual save
  const performSave = async (data: MasterDataContactFormData, unsetPreviousPrimary?: { contactId: number }) => {
    setSaving(true);
    try {
      // #92: completed affiliation rows — validated non-empty in onSubmit.
      // The FIRST row is the primary affiliation and drives the API route.
      const affiliationRows = (data.affiliations ?? []).filter((row) => row.companyId !== null);
      const primaryRow = affiliationRows[0];
      if (!primaryRow || primaryRow.companyId === null) {
        message.error(tContacts('validation.affiliationRequired'));
        return;
      }
      const affiliationsPayload: ContactAffiliationInput[] = affiliationRows.map((row, index) => ({
        companyType: row.companyType,
        companyId: row.companyId as number,
        roleTitle: row.roleTitle || undefined,
        isPrimary: index === 0,
      }));

      // First, unset the previous primary contact if needed
      if (unsetPreviousPrimary && data.isPrimary && primaryRow?.companyId) {
        if (primaryRow.companyType === 'vendor') {
          await setVendorContactPrimary(primaryRow.companyId, unsetPreviousPrimary.contactId, false);
        } else {
          await setCustomerContactPrimary(
            primaryRow.companyId,
            unsetPreviousPrimary.contactId,
            false
          );
        }
      }

      const addressLang = i18n.language.startsWith('zh') ? 'zh' : 'en';
      const contactData = {
        isPrimary: data.isPrimary,
        name: data.name,
        jobTitle: data.jobTitle,
        phoneCountryCode: data.phoneCountryCode,
        phoneNumber: data.phoneNumber,
        phoneExtension: data.phoneExtension || undefined,
        mobileCountryCode: data.mobileCountryCode || undefined,
        mobileNumber: data.mobileNumber || undefined,
        fax: data.fax || undefined,
        email: data.email,
        countryId: data.countryId || undefined,
        stateId: data.stateId || undefined,
        cityId: data.cityId || undefined,
        postcode: data.postcode || undefined,
        address: data.address || undefined,
        addressLang,
        isActive: true,
        affiliations: affiliationsPayload,
      };

      if (isEditMode && contact) {
        // Update existing contact — routed by the primary affiliation
        if (primaryRow.companyType === 'vendor') {
          await updateVendorContact(primaryRow.companyId as number, contact.contactId, contactData);
        } else {
          await updateCustomerContact(
            primaryRow.companyId as number,
            contact.contactId,
            contactData
          );
        }
        message.success(t('vendor.messages.contactUpdatedSuccess'));
      } else {
        // Create new contact — routed by the primary affiliation
        if (primaryRow.companyType === 'vendor') {
          await createVendorContact(primaryRow.companyId as number, contactData);
        } else {
          await createCustomerContact(primaryRow.companyId as number, contactData);
        }
        message.success(t('vendor.messages.contactCreatedSuccess'));
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
      message.error(t('vendor.messages.contactSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = async (data: MasterDataContactFormData) => {
    // #92: at least one completed affiliation row is required, every row must
    // have a company, and the same company cannot be linked twice
    const rows = data.affiliations ?? [];
    const completedRows = rows.filter((row) => row.companyId !== null);
    if (completedRows.length === 0) {
      message.error(tContacts('validation.affiliationRequired'));
      return;
    }
    if (completedRows.length !== rows.length) {
      message.error(tContacts('validation.affiliationCompanyRequired'));
      return;
    }
    const seen = new Set<string>();
    for (const row of completedRows) {
      const key = `${row.companyType}:${row.companyId}`;
      if (seen.has(key)) {
        message.error(tContacts('validation.affiliationDuplicate'));
        return;
      }
      seen.add(key);
    }

    // Check if setting isPrimary to true and there's already a primary contact
    const isSettingNewPrimary = data.isPrimary && existingPrimaryContact;

    // If setting new primary, show confirmation modal
    if (isSettingNewPrimary) {
      modal.confirm({
        title: t('vendor.modals.primaryContactExists'),
        icon: <InfoCircleOutlined style={{ color: token.colorPrimary }} />,
        content: t('vendor.modals.replacePrimaryContact', {
          current: existingPrimaryContact.name,
          new: data.name,
        }),
        okText: t('vendor.modals.replacePrimary'),
        okType: 'primary',
        cancelText: t('vendor.actions.cancel'),
        okButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        cancelButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        onOk: async () => {
          await performSave(data, existingPrimaryContact);
        },
      });
    } else {
      // No primary conflict, save directly
      await performSave(data);
    }
  };

  const countryCodeOptions = getCountryCodeOptions(t);

  // #92: per-row company options (each affiliation row picks its own side)
  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.vendorId, label: v.vendorName })),
    [vendors]
  );
  const customerOptions = useMemo(
    () => customers.map((c) => ({ value: c.customerId, label: c.customerName })),
    [customers]
  );
  const affiliationTypeOptions = [
    { value: 'vendor' as const, label: tContacts('tabs.vendor') },
    { value: 'customer' as const, label: tContacts('tabs.customer') },
  ];

  // Footer buttons
  const footerButtons = readOnly ? (
    <div className="tw-flex tw-justify-end">
      <Button
        onClick={onClose}
        style={{
          height: `${controlHeightTokens.controlHeightXL}px`,
          fontSize: token.fontSizeSM,
          borderRadius: `${radiusTokens.card}px`,
        }}
      >
        {t('vendor.actions.close')}
      </Button>
    </div>
  ) : (
    <div className="tw-flex tw-justify-end tw-gap-2">
      <Button
        onClick={onClose}
        style={{
          height: `${controlHeightTokens.controlHeightXL}px`,
          fontSize: token.fontSizeSM,
          borderRadius: `${radiusTokens.card}px`,
        }}
      >
        {t('vendor.actions.cancel')}
      </Button>
      <Button
        type="primary"
        htmlType="submit"
        form="master-data-contact-form"
        loading={saving}
        style={{
          height: `${controlHeightTokens.controlHeightXL}px`,
          fontSize: token.fontSizeSM,
          borderRadius: `${radiusTokens.card}px`,
        }}
      >
        {isEditMode ? t('vendor.actions.save') : t('vendor.modals.add')}
      </Button>
    </div>
  );

  // Modal title
  const modalTitle = readOnly
    ? tContacts('modals.viewContact')
    : isEditMode
      ? tContacts('modals.editContact')
      : tContacts('modals.addContact');

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={modalTitle}
      footer={footerButtons}
      width={900}
      styles={{
        body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', overflowX: 'hidden' },
      }}
    >
      <style>
        {`
          .master-data-contact-form-modal .ant-select-selector {
            height: ${controlHeightTokens.controlHeightXL}px !important;
            display: flex !important;
            align-items: center !important;
          }
          .master-data-contact-form-modal .ant-input-number {
            width: 100%;
          }
          .master-data-contact-form-modal .ant-input-number-input {
            height: ${controlHeightTokens.controlHeightXL - 2}px !important;
          }
          .ant-select-dropdown .ant-select-item {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 10px 12px !important;
            font-size: ${token.fontSize}px !important;
            line-height: ${controlHeightTokens.controlHeightXL - 20}px !important;
            display: flex !important;
            align-items: center !important;
          }
        `}
      </style>

      <form
        id="master-data-contact-form"
        className="master-data-contact-form-modal"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(onSubmit)(e);
        }}
      >
        {/* Basic Information Container */}
        <div style={formStyles.fieldContainerStyle}>
          <Typography.Text strong style={formStyles.labelStyle}>
            {t('vendor.sections.basicInformation')}
          </Typography.Text>
          {/* Row 1: Name + Primary */}
          <Row gutter={12}>
            <Col span={18}>
              <Typography.Text
                style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
              >
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.contactName')}
              </Typography.Text>
              <Controller
                name="name"
                control={control}
                rules={{ required: t('vendor.validation.nameRequired') }}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      size="large"
                      readOnly={readOnly}
                      placeholder={t('vendor.placeholders.contactName')}
                      style={{
                        ...formStyles.inputStyle,
                        ...readOnlyStyle,
                                                borderColor: errors.name ? token.colorError : undefined,
                      }}
                    />
                    {!readOnly && errors.name && (touchedFields.name || isSubmitted) && (
                      <Typography.Text
                        style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                      >
                        {t(errors.name.message!)}
                      </Typography.Text>
                    )}
                  </>
                )}
              />
            </Col>
            <Col span={6}>
              <Typography.Text
                style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
              >
                {t('vendor.fields.primary')}
              </Typography.Text>
              <Controller
                name="isPrimary"
                control={control}
                render={({ field }) => (
                  <Radio.Group
                    {...field}
                    value={field.value}
                    onChange={(e) => !readOnly && field.onChange(e.target.value)}
                    className={readOnly ? 'form-readonly-radio' : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: `${controlHeightTokens.controlHeightXL}px`,
                                          }}
                  >
                    <Radio value={true}>{t('vendor.fields.yes')}</Radio>
                    <Radio value={false}>{t('vendor.fields.no')}</Radio>
                  </Radio.Group>
                )}
              />
            </Col>
          </Row>

          {/* Row 3: Job Title */}
          <Row gutter={12} style={{ marginTop: '12px' }}>
            <Col span={24}>
              <Typography.Text
                style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
              >
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.jobTitle')}
              </Typography.Text>
              <Controller
                name="jobTitle"
                control={control}
                rules={{ required: t('vendor.validation.jobTitleRequired') }}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      size="large"
                      readOnly={readOnly}
                      placeholder={t('vendor.placeholders.jobTitle')}
                      style={{
                        ...formStyles.inputStyle,
                        ...readOnlyStyle,
                                                borderColor: errors.jobTitle ? token.colorError : undefined,
                      }}
                    />
                    {!readOnly && errors.jobTitle && (touchedFields.jobTitle || isSubmitted) && (
                      <Typography.Text
                        style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                      >
                        {t(errors.jobTitle.message!)}
                      </Typography.Text>
                    )}
                  </>
                )}
              />
            </Col>
          </Row>
        </div>

        {/* Affiliations Container (#92): one person, several companies —
            vendor(s) and customer(s) at once. First row = primary affiliation
            (drives the legacy single-org route + the primary-contact check) */}
        <div style={formStyles.fieldContainerStyle}>
          <Typography.Text strong style={formStyles.labelStyle}>
            {tContacts('sections.affiliations')}
          </Typography.Text>
          <Row gutter={8}>
            <Col span={5}>
              <Typography.Text
                style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
              >
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {tContacts('fields.affiliationType')}
              </Typography.Text>
            </Col>
            <Col span={10}>
              <Typography.Text
                style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
              >
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {tContacts('fields.affiliationCompany')}
              </Typography.Text>
            </Col>
            <Col span={7}>
              <Typography.Text
                style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
              >
                {tContacts('fields.roleTitle')}
              </Typography.Text>
            </Col>
            <Col span={2} />
          </Row>
          {affiliationFields.map((affiliationField, index) => (
            <Row
              gutter={8}
              key={affiliationField.id}
              style={{ marginTop: index === 0 ? 0 : '12px' }}
              align="top"
            >
              <Col span={5}>
                <Controller
                  name={`affiliations.${index}.companyType`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      size="large"
                      open={readOnly ? false : undefined}
                      className={readOnly ? 'form-readonly-select' : undefined}
                      options={affiliationTypeOptions}
                      onChange={(value) => {
                        if (readOnly) return;
                        field.onChange(value);
                        // The company list follows the side — clear stale pick
                        setValue(`affiliations.${index}.companyId`, null);
                      }}
                      style={{ ...formStyles.inputStyle }}
                    />
                  )}
                />
              </Col>
              <Col span={10}>
                <Controller
                  name={`affiliations.${index}.companyId`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      size="large"
                      showSearch
                      allowClear={!readOnly}
                      open={readOnly ? false : undefined}
                      className={readOnly ? 'form-readonly-select' : undefined}
                      loading={
                        watchedAffiliations?.[index]?.companyType === 'customer'
                          ? customersLoading
                          : vendorsLoading
                      }
                      placeholder={tContacts('placeholders.selectCompany')}
                      options={
                        watchedAffiliations?.[index]?.companyType === 'customer'
                          ? customerOptions
                          : vendorOptions
                      }
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      style={{ ...formStyles.inputStyle }}
                    />
                  )}
                />
              </Col>
              <Col span={7}>
                <Controller
                  name={`affiliations.${index}.roleTitle`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      readOnly={readOnly}
                      placeholder={tContacts('placeholders.roleTitle')}
                      style={{ ...formStyles.inputStyle, ...readOnlyStyle }}
                    />
                  )}
                />
              </Col>
              <Col span={2}>
                {!readOnly && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={tContacts('actions.removeAffiliation')}
                    disabled={affiliationFields.length <= 1}
                    onClick={() => removeAffiliation(index)}
                    style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                  />
                )}
              </Col>
            </Row>
          ))}
          {!readOnly && (
            <Button
              type="dashed"
              block
              icon={<PlusOutlined />}
              onClick={() =>
                appendAffiliation({ companyType: 'vendor', companyId: null, roleTitle: '' })
              }
              style={{
                marginTop: '12px',
                height: `${controlHeightTokens.controlHeightXL}px`,
                borderRadius: `${radiusTokens.card}px`,
              }}
            >
              {tContacts('actions.addAffiliation')}
            </Button>
          )}
        </div>

        {/* Contact Information Container */}
        <div style={formStyles.fieldContainerStyle}>
          <Typography.Text strong style={formStyles.labelStyle}>
            {t('vendor.sections.contactDetails')}
          </Typography.Text>

          {/* Phone */}
          <div>
            <Row gutter={8}>
              <Col span={6}>
                <Typography.Text
                  style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
                >
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('vendor.fields.phoneCountryCode')}
                </Typography.Text>
              </Col>
              <Col span={14}>
                <Typography.Text
                  style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
                >
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('vendor.fields.phoneNumberLabel')}
                </Typography.Text>
              </Col>
              <Col span={4}>
                <Typography.Text
                  style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
                >
                  {t('vendor.fields.ext')}
                </Typography.Text>
              </Col>
            </Row>
            <Row gutter={8} style={{ }}>
              <Col span={6}>
                <Controller
                  name="phoneCountryCode"
                  control={control}
                  rules={{ required: t('vendor.validation.codeRequired') }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      size="large"
                      showSearch={!readOnly}
                      open={readOnly ? false : undefined}
                      className={readOnly ? 'form-readonly-select' : undefined}
                      options={countryCodeOptions}
                      placeholder={t('vendor.placeholders.code')}
                      style={{
                        ...formStyles.inputStyle,
                        borderColor: errors.phoneCountryCode ? token.colorError : undefined,
                      }}
                    />
                  )}
                />
              </Col>
              <Col span={14}>
                <Controller
                  name="phoneNumber"
                  control={control}
                  rules={{ required: t('vendor.validation.phoneRequired') }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      readOnly={readOnly}
                      placeholder={t('vendor.placeholders.phoneNumber')}
                      onChange={(e) => {
                        if (readOnly) return;
                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                        field.onChange(numericValue);
                      }}
                      style={{
                        ...formStyles.inputStyle,
                        ...readOnlyStyle,
                        borderColor: errors.phoneNumber ? token.colorError : undefined,
                      }}
                    />
                  )}
                />
              </Col>
              <Col span={4}>
                <Controller
                  name="phoneExtension"
                  control={control}
                  render={({ field }) => (
                    <Input
                      size="large"
                      {...field}
                      readOnly={readOnly}
                      placeholder={t('vendor.placeholders.ext')}
                      onChange={(e) => {
                        if (readOnly) return;
                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                        field.onChange(numericValue);
                      }}
                      style={{ ...formStyles.inputStyle, ...readOnlyStyle }}
                    />
                  )}
                />
              </Col>
            </Row>
            {!readOnly && (errors.phoneCountryCode || errors.phoneNumber) && (touchedFields.phoneCountryCode || touchedFields.phoneNumber || isSubmitted) && (
              <Typography.Text
                style={{ fontSize: token.fontSizeSM, color: token.colorError }}
              >
                {t((errors.phoneCountryCode?.message || errors.phoneNumber?.message)!)}
              </Typography.Text>
            )}
          </div>

          {/* Mobile with Country Code */}
          <div style={{ marginTop: '12px' }}>
            <Row gutter={8}>
              <Col span={6}>
                <Typography.Text
                  style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
                >
                  {t('vendor.fields.mobileCountryCode')}
                </Typography.Text>
              </Col>
              <Col span={18}>
                <Typography.Text
                  style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
                >
                  {t('vendor.fields.mobileNumberLabel')}
                </Typography.Text>
              </Col>
            </Row>
            <Row gutter={8} style={{ }}>
              <Col span={6}>
                <Controller
                  name="mobileCountryCode"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      size="large"
                      showSearch={!readOnly}
                      open={readOnly ? false : undefined}
                      className={readOnly ? 'form-readonly-select' : undefined}
                      options={countryCodeOptions}
                      placeholder={t('vendor.placeholders.code')}
                      style={{
                        ...formStyles.inputStyle,
                        borderColor: errors.mobileCountryCode ? token.colorError : undefined,
                      }}
                    />
                  )}
                />
              </Col>
              <Col span={18}>
                <Controller
                  name="mobileNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      readOnly={readOnly}
                      placeholder={t('vendor.placeholders.mobileNumber')}
                      onChange={(e) => {
                        if (readOnly) return;
                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                        field.onChange(numericValue);
                      }}
                      style={{
                        ...formStyles.inputStyle,
                        ...readOnlyStyle,
                        borderColor: errors.mobileNumber ? token.colorError : undefined,
                      }}
                    />
                  )}
                />
              </Col>
            </Row>
          </div>

          {/* Fax and Email in one row */}
          <Row gutter={12} style={{ marginTop: '12px' }}>
            <Col span={12}>
              <Typography.Text
                style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
              >
                {t('vendor.fields.fax')}
              </Typography.Text>
              <Controller
                name="fax"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    readOnly={readOnly}
                    placeholder={t('vendor.placeholders.faxNumber')}
                    style={{ ...formStyles.inputStyle, ...readOnlyStyle, }}
                  />
                )}
              />
            </Col>
            <Col span={12}>
              <Typography.Text
                style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
              >
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.email')}
              </Typography.Text>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: t('vendor.validation.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('vendor.validation.invalidEmail'),
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    readOnly={readOnly}
                    placeholder={t('vendor.placeholders.emailAddress')}
                    style={{
                      ...formStyles.inputStyle,
                      ...readOnlyStyle,
                                            borderColor: errors.email ? token.colorError : undefined,
                    }}
                  />
                )}
              />
              {!readOnly && errors.email && (touchedFields.email || isSubmitted) && (
                <Typography.Text
                  style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                >
                  {t(errors.email.message!)}
                </Typography.Text>
              )}
            </Col>
          </Row>
        </div>

        {/* Address Container */}
        <div style={formStyles.fieldContainerStyle}>
          <Typography.Text strong style={formStyles.labelStyle}>
            {t('vendor.fields.address')}
          </Typography.Text>

          {/* Country Select (single-address entry group, countryId mode) —
              state/city field names are passed ONLY so a country change clears
              the stale parse outputs (board2-#79) */}
          <CascadingLocationFields
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
            countryFieldName="countryId"
            countryLabelFieldName="country"
            stateFieldName="stateId"
            stateLabelFieldName="state"
            cityFieldName="cityId"
            cityLabelFieldName="city"
            useCountryId={true}
            countryRequired={false}
            readOnly={readOnly}
            langOverride={contact?.addressLang}
          />

          {/* Row: Postcode (the lookup input feeding the parse simulation) */}
          <Row gutter={12} style={{ marginTop: '12px' }}>
            <Col span={8}>
              <Typography.Text
                style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
              >
                {t('vendor.fields.postcode')}
              </Typography.Text>
              <Controller
                name="postcode"
                control={control}
                render={({ field }) => (
                  <Input
                    size="large"
                    {...field}
                    readOnly={readOnly}
                    onChange={(e) => {
                      field.onChange(e);
                      if (!readOnly) handlePostcodeChange(e.target.value);
                    }}
                    onBlur={() => {
                      field.onBlur();
                      if (!readOnly) handlePostcodeBlur(String(field.value ?? ''));
                    }}
                    placeholder={t('vendor.placeholders.postcode')}
                    style={{
                      ...formStyles.inputStyle,
                      ...readOnlyStyle,
                      width: '100%',
                                          }}
                  />
                )}
              />
            </Col>
          </Row>

          {/* Row: single full-address textfield — the whole street address in
              ONE field; the backend splits it into stateId/cityId (board2-#79) */}
          <Row gutter={12} style={{ marginTop: '12px' }}>
            <Col span={24}>
              <Typography.Text
                style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
              >
                {t('vendor.fields.address')}
              </Typography.Text>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <Input
                    size="large"
                    {...field}
                    readOnly={readOnly}
                    placeholder={t('vendor.placeholders.address')}
                    style={{
                      ...formStyles.inputStyle,
                      ...readOnlyStyle,
                      width: '100%',
                                          }}
                  />
                )}
              />
            </Col>
          </Row>
        </div>
      </form>
    </Modal>
  );
};

export default MasterDataContactFormModal;
